// features/procurement/ReturnDetailPage.tsx
/**
 * Purchase Return Detail — Redesigned Executive View
 * Features:
 * - Interactive multi-stage status timeline (Draft -> Approved -> Shipped -> Credit Received)
 * - Stock deduction disclaimer with high-polish confirmation modal
 * - Return value breakdown & credit claim calculation
 * - Item-by-item fault reasons and quantities
 * - Zero silent failures with explicit notifications
 */

import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Spinner } from '@/components/ui/Spinner';
import { ErrorState } from '@/components/ui/ErrorState';
import {
    ArrowLeft, RotateCcw, Store, User, Calendar,
    CheckCircle2, Clock, Package, Building2,
    Truck, AlertTriangle, ShieldCheck, Sparkles,
    ChevronRight, X
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';
import {
    getReturn,
    approveReturn,
    getProcurementErrorMessage,
    PurchaseReturn,
} from './services/procurementApi';

const FLOW_STEPS = ['draft', 'approved', 'shipped', 'received_by_vendor', 'credit_note_received'];

const STATUS_CONFIG: Record<string, { label: string; bg: string; text: string; border: string; icon: any }> = {
    draft: { label: 'Draft Return', bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', icon: Clock },
    approved: { label: 'Approved (Stock Deducted)', bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', icon: CheckCircle2 },
    shipped: { label: 'Dispatched to Supplier', bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200', icon: Package },
    received_by_vendor: { label: 'Supplier Acknowledged', bg: 'bg-cyan-50', text: 'text-cyan-700', border: 'border-cyan-200', icon: Truck },
    credit_note_received: { label: 'Credit Note Settled', bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', icon: CheckCircle2 },
};

export function ReturnDetailPage() {
    const { returnId } = useParams<{ returnId: string }>();
    const navigate = useNavigate();
    const user = useAuthStore((s) => s.user);
    const queryClient = useQueryClient();
    const addToast = useToastStore((s) => s.addToast);

    const [showConfirmModal, setShowConfirmModal] = useState(false);

    // ============================================================
    // QUERY
    // ============================================================

    const { data: ret, isLoading, isError, refetch } = useQuery<PurchaseReturn>({
        queryKey: ['return-detail', returnId],
        queryFn: () => getReturn(user!.tenantId, returnId!),
        enabled: !!user?.tenantId && !!returnId,
    });

    // ============================================================
    // MUTATION
    // ============================================================

    const approveMutation = useMutation({
        mutationFn: () => approveReturn(user!.tenantId, returnId!),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['return-detail', returnId] });
            queryClient.invalidateQueries({ queryKey: ['returns-list'] });
            queryClient.invalidateQueries({ queryKey: ['stock-levels'] });
            setShowConfirmModal(false);
            addToast({
                type: 'success',
                title: 'Return Approved & Stock Deducted',
                message: `Inventory reduced for returned items on #${ret?.returnNumber}.`,
            });
        },
        onError: (err: any) => {
            setShowConfirmModal(false);
            addToast({
                type: 'error',
                title: 'Approval Failed',
                message: getProcurementErrorMessage(err),
            });
        },
    });

    // ============================================================
    // HELPERS
    // ============================================================

    const formatNaira = (val: string | number | undefined) => {
        if (!val) return '₦0.00';
        const num = typeof val === 'string' ? parseFloat(val) : val;
        return isNaN(num) ? '₦0.00' : `₦${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const formatDate = (d: string | null | undefined) => {
        if (!d) return '—';
        return new Date(d).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' });
    };

    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[70vh] bg-slate-50/60 text-slate-600 gap-4">
                <Spinner size="lg" variant="teal" />
                <p className="text-sm font-semibold tracking-wide text-slate-700">Loading purchase return record...</p>
            </div>
        );
    }

    if (isError || !ret) {
        return (
            <div className="min-h-screen bg-slate-50/60 flex items-center justify-center p-6">
                <ErrorState
                    title="Return Record Not Found"
                    message="The requested purchase return document could not be loaded or has been deleted."
                    onBack={() => navigate('/procurement/returns')}
                    onRetry={() => refetch()}
                />
            </div>
        );
    }

    const currentStepIndex = FLOW_STEPS.indexOf(ret.status);
    const statusCfg = STATUS_CONFIG[ret.status] || STATUS_CONFIG.draft;
    const StatusIcon = statusCfg.icon;

    const totalReturnQty = ret.items?.reduce((s, item) => s + parseFloat(item.quantity || '0'), 0) || 0;
    const totalReturnValue = ret.items?.reduce((s, item) => {
        const qty = parseFloat(item.quantity || '0');
        const cost = parseFloat(item.unitCost || '0');
        return s + (qty * (isNaN(cost) ? 0 : cost));
    }, 0) || 0;

    return (
        <div className="min-h-screen bg-slate-50/60 text-slate-800 pb-20">
            {/* Top Action Bar */}
            <div className="sticky top-0 z-30 bg-white/95 backdrop-blur-xl border-b border-slate-200 shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => navigate('/procurement/returns')}
                                className="p-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 hover:text-slate-900 border border-slate-200 transition-all shadow-sm"
                                type="button"
                                title="Back to Returns List"
                            >
                                <ArrowLeft className="w-5 h-5" />
                            </button>
                            <div className="p-2.5 bg-gradient-to-br from-rose-500 to-pink-600 rounded-2xl shadow-lg shadow-rose-500/20 text-white">
                                <RotateCcw className="w-6 h-6" />
                            </div>
                            <div>
                                <div className="flex items-center gap-2.5 flex-wrap">
                                    <h1 className="text-xl sm:text-2xl font-black tracking-tight text-slate-900 font-mono">
                                        #{ret.returnNumber}
                                    </h1>
                                    <span className={clsx('px-3 py-1 rounded-full text-xs font-bold border flex items-center gap-1.5', statusCfg.bg, statusCfg.text, statusCfg.border)}>
                                        <StatusIcon className="w-3.5 h-3.5" />
                                        <span>{statusCfg.label}</span>
                                    </span>
                                </div>
                                <p className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
                                    <span className="font-semibold text-slate-700">{ret.vendorName || 'Supplier'}</span>
                                    <span>•</span>
                                    <span>{ret.branchName || 'Branch'}</span>
                                    <span>•</span>
                                    <span>Initiated {formatDate(ret.createdAt)}</span>
                                </p>
                            </div>
                        </div>

                        {/* Top CTAs */}
                        <div className="flex items-center gap-3 self-end sm:self-auto">
                            {ret.status === 'draft' && (
                                <button
                                    onClick={() => setShowConfirmModal(true)}
                                    className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white rounded-xl font-bold text-xs shadow-lg shadow-emerald-500/20 transition-all cursor-pointer"
                                    type="button"
                                >
                                    <CheckCircle2 className="w-4 h-4" />
                                    <span>Approve & Deduct Stock</span>
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 space-y-8">
                {/* Status Timeline */}
                <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm space-y-4">
                    <div className="flex items-center justify-between text-xs text-slate-500">
                        <span className="font-bold uppercase tracking-wider text-rose-600 flex items-center gap-1.5">
                            <Sparkles className="w-4 h-4" />
                            <span>Return Settlement Pipeline</span>
                        </span>
                        <span>{totalReturnQty.toLocaleString()} units claimed</span>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                        {FLOW_STEPS.map((step, idx) => {
                            const isDone = currentStepIndex >= 0 && idx <= currentStepIndex;
                            const isCurrent = idx === currentStepIndex;

                            return (
                                <div key={step} className="flex items-center gap-2">
                                    <div className={clsx(
                                        'px-3.5 py-1.5 rounded-xl text-xs font-bold border transition-all flex items-center gap-2',
                                        isCurrent
                                            ? 'bg-rose-50 border-rose-300 text-rose-700 shadow-sm'
                                            : isDone
                                                ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                                                : 'bg-slate-50 border-slate-200 text-slate-400'
                                    )}>
                                        <div className={clsx(
                                            'w-2 h-2 rounded-full',
                                            isCurrent ? 'bg-rose-500 animate-pulse' : isDone ? 'bg-emerald-500' : 'bg-slate-300'
                                        )} />
                                        <span>{step.replace(/_/g, ' ').toUpperCase()}</span>
                                    </div>
                                    {idx < FLOW_STEPS.length - 1 && (
                                        <ChevronRight className={clsx('w-4 h-4', isDone ? 'text-rose-400' : 'text-slate-300')} />
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Scorecards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
                        <div className="flex items-center gap-2 text-slate-500 text-xs uppercase font-bold mb-1">
                            <Building2 className="w-3.5 h-3.5 text-rose-600" /> Vendor
                        </div>
                        <p className="font-bold text-slate-900 text-sm truncate">{ret.vendorName || 'Supplier'}</p>
                    </div>

                    <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
                        <div className="flex items-center gap-2 text-slate-500 text-xs uppercase font-bold mb-1">
                            <Store className="w-3.5 h-3.5 text-teal-600" /> Source Facility
                        </div>
                        <p className="font-bold text-slate-900 text-sm">{ret.branchName || 'Main Warehouse'}</p>
                    </div>

                    <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
                        <div className="flex items-center gap-2 text-slate-500 text-xs uppercase font-bold mb-1">
                            <User className="w-3.5 h-3.5 text-blue-600" /> Filed By
                        </div>
                        <p className="font-bold text-slate-900 text-sm">{ret.createdByName || 'Staff'}</p>
                    </div>

                    <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
                        <div className="flex items-center gap-2 text-slate-500 text-xs uppercase font-bold mb-1">
                            <Calendar className="w-3.5 h-3.5 text-amber-600" /> Created Date
                        </div>
                        <p className="font-bold text-slate-900 text-sm">{formatDate(ret.createdAt)}</p>
                    </div>
                </div>

                {/* Return Items Table */}
                <div className="bg-white border border-slate-200 rounded-3xl p-6 lg:p-8 shadow-sm space-y-6">
                    <div className="flex items-center justify-between pb-4 border-b border-slate-200">
                        <div>
                            <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                                <Package className="w-5 h-5 text-rose-600" />
                                <span>Returned Products ({ret.items?.length || 0})</span>
                            </h3>
                            <p className="text-xs text-slate-500">Inventory lines removed and billed back to supplier account</p>
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                            <thead>
                                <tr className="border-b border-slate-200 text-[11px] font-bold text-slate-500 uppercase tracking-wider bg-slate-50/50">
                                    <th className="py-3 px-4">Product Catalog</th>
                                    <th className="py-3 px-4 text-right">Quantity Returned</th>
                                    <th className="py-3 px-4 text-right">Unit Rate</th>
                                    <th className="py-3 px-4 text-right">Credit Amount</th>
                                    <th className="py-3 px-4">Fault Cause / Reason</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {ret.items?.map((item) => {
                                    const qty = parseFloat(item.quantity || '0');
                                    const cost = parseFloat(item.unitCost || '0');
                                    const total = qty * cost;

                                    return (
                                        <tr key={item.id} className="hover:bg-slate-50 transition-colors">
                                            <td className="py-4 px-4">
                                                <p className="font-bold text-slate-900 text-sm">{item.productName || 'Inventory Product'}</p>
                                                <p className="text-xs text-slate-500 font-mono">UOM: {item.uomName || 'Unit'}</p>
                                            </td>
                                            <td className="py-4 px-4 text-right font-bold text-rose-600">
                                                {qty.toLocaleString()}
                                            </td>
                                            <td className="py-4 px-4 text-right font-mono text-slate-600">
                                                {formatNaira(item.unitCost)}
                                            </td>
                                            <td className="py-4 px-4 text-right font-bold text-slate-900 font-mono">
                                                {formatNaira(total)}
                                            </td>
                                            <td className="py-4 px-4 text-xs text-slate-700">
                                                <span className="px-2.5 py-1 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 font-medium">
                                                    {item.reason || ret.reason || 'Damaged / Defect'}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Financial Summary */}
                    <div className="pt-6 border-t border-slate-200 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                        <div className="text-xs text-slate-500 space-y-2 max-w-md">
                            {ret.reason && (
                                <p className="text-slate-700">
                                    <span className="font-bold text-slate-900">General Return Reason: </span>
                                    {ret.reason}
                                </p>
                            )}
                            <p className="text-[11px] text-slate-400">
                                Stock deduction is audited in compliance with ISO warehouse inventory standards.
                            </p>
                        </div>

                        <div className="w-full md:w-80 bg-slate-50 border border-slate-200 rounded-2xl p-5 space-y-2.5">
                            <div className="flex items-center justify-between text-xs text-slate-600">
                                <span>Total Units Returned</span>
                                <span className="font-semibold text-slate-900">{totalReturnQty.toLocaleString()} units</span>
                            </div>
                            <div className="pt-3 border-t border-slate-200 flex items-center justify-between">
                                <span className="text-sm font-bold text-slate-900">Total Credit Claim</span>
                                <span className="text-base font-black text-rose-600">{formatNaira(totalReturnValue)}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ========== CONFIRMATION MODAL ========== */}
            <AnimatePresence>
                {showConfirmModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
                        onClick={() => setShowConfirmModal(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0, y: 20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.95, opacity: 0, y: 20 }}
                            onClick={(e) => e.stopPropagation()}
                            className="bg-white border border-slate-200 rounded-3xl shadow-2xl w-full max-w-md p-6 sm:p-8 text-center space-y-5"
                        >
                            <div className="w-16 h-16 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center mx-auto">
                                <AlertTriangle className="w-8 h-8" />
                            </div>

                            <div>
                                <h2 className="text-xl font-bold text-slate-900">Approve Return & Deduct Stock?</h2>
                                <p className="text-xs text-slate-500 mt-2">
                                    Approving Return #{ret.returnNumber} will immediately <strong>deduct {totalReturnQty.toLocaleString()} unit(s)</strong> from the physical inventory of {ret.branchName || 'your branch'} and issue a debit claim for {formatNaira(totalReturnValue)}.
                                </p>
                            </div>

                            <div className="flex items-center gap-3 pt-2">
                                <button
                                    onClick={() => setShowConfirmModal(false)}
                                    className="flex-1 px-4 py-3 rounded-xl border border-slate-300 hover:bg-slate-100 text-slate-700 font-semibold text-sm transition-all"
                                    type="button"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => approveMutation.mutate()}
                                    disabled={approveMutation.isPending}
                                    className="flex-1 px-4 py-3 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 text-white hover:from-emerald-500 hover:to-teal-500 font-bold text-sm shadow-xl shadow-emerald-500/20 transition-all flex items-center justify-center gap-2 cursor-pointer"
                                    type="button"
                                >
                                    {approveMutation.isPending ? (
                                        <Spinner size="xs" variant="white" />
                                    ) : (
                                        'Approve & Deduct'
                                    )}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}